import { Dispatch, SetStateAction, useState } from 'react';

/**
 * `useState` that snaps back to `initialState` whenever `resetKey` changes.
 *
 * ```ts
 * // the cursor is thrown away as soon as the sort or the search term changes
 * const [cursor, setCursor] = useStateResetOn(`${sort}|${searchValue}`, null);
 * ```
 *
 * Use it for state that is only valid for one set of query inputs, e.g. a pagination cursor,
 * when those inputs live somewhere that can change without this component unmounting, such as the
 * URL or a parent's props.
 *
 * The reset runs during render rather than in a `useEffect`, and the value returned on that same
 * render is already the reset one. Reason: callers pass this state down to `useSuspenseQuery`
 * children, so a single stale render is enough to fire a request built from the old value — and
 * with an error boundary in the tree, that failed request wins the race.
 *
 * @param resetKey Serialized form of the inputs this state is valid for.
 * @param initialState Value returned on mount and after every reset.
 */
export function useStateResetOn<T>(resetKey: string, initialState: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(initialState);
  const [trackedResetKey, setTrackedResetKey] = useState<string>(resetKey);

  const isStale = trackedResetKey !== resetKey;

  if (isStale) {
    setTrackedResetKey(resetKey);
    setState(initialState);
  }

  return [isStale ? initialState : state, setState];
}
